import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import { format, parseISO } from 'date-fns';

const SchedulingInsights = ({ tasks, familyMembers }) => {
  // Calculate peak activity times
  const peakActivityData = useMemo(() => {
    const hourCounts = {};
    tasks?.forEach(task => {
      const hour = Math.floor(Math.random() * 14) + 6; // Mock: 6 AM to 8 PM
      hourCounts[hour] = (hourCounts?.[hour] || 0) + 1;
    });

    const sortedHours = Object.entries(hourCounts)
      ?.sort((a, b) => b?.[1] - a?.[1])
      ?.slice(0, 3);

    return sortedHours?.map(([hour, count]) => ({
      hour: parseInt(hour),
      count,
      label: `${hour}:00 - ${parseInt(hour) + 1}:00`
    }));
  }, [tasks]);

  // Calculate most productive days
  const productiveDays = useMemo(() => {
    const dayCounts = {};
    tasks?.forEach(task => {
      if (task?.completed) {
        const day = format(parseISO(task?.date), 'EEEE');
        dayCounts[day] = (dayCounts?.[day] || 0) + 1;
      }
    });

    return Object.entries(dayCounts)
      ?.sort((a, b) => b?.[1] - a?.[1])
      ?.slice(0, 3)
      ?.map(([day, count]) => ({ day, count }));
  }, [tasks]);

  // Category breakdown
  const categoryBreakdown = useMemo(() => {
    const categories = {};
    tasks?.forEach(task => {
      const cat = task?.category || 'Other';
      categories[cat] = (categories?.[cat] || 0) + 1;
    });

    return Object.entries(categories)
      ?.sort((a, b) => b?.[1] - a?.[1])
      ?.slice(0, 5)
      ?.map(([category, count]) => ({
        category,
        count,
        percentage: Math.round((count / tasks?.length) * 100)
      }));
  }, [tasks]);

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="bar-chart-2" size={24} className="text-secondary" />
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">
            Scheduling Insights
          </h2>
          <p className="text-sm text-muted-foreground">Activity patterns and trends</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Peak Activity Times */}
        <div>
          <h3 className="text-base md:text-lg font-medium text-foreground mb-3 flex items-center gap-2">
            <Icon name="clock" size={18} className="text-primary" />
            Peak Activity Times
          </h3>
          <div className="space-y-2">
            {peakActivityData?.map((item, index) => (
              <div key={item?.hour} className="flex items-center gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-foreground">{item?.label}</span>
                    <span className="text-sm text-muted-foreground">{item?.count} tasks</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-primary to-secondary h-full rounded-full"
                      style={{ width: `${(item?.count / tasks?.length) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Most Productive Days */}
        <div className="pt-6 border-t border-border">
          <h3 className="text-base md:text-lg font-medium text-foreground mb-3 flex items-center gap-2">
            <Icon name="calendar-check" size={18} className="text-success" />
            Most Productive Days
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {productiveDays?.map((item, index) => (
              <div
                key={item?.day}
                className="bg-gradient-to-br from-success/10 to-success/5 rounded-lg p-3 border border-success/20"
              >
                <div className="text-2xl font-bold text-success mb-1">{item?.count}</div>
                <div className="text-xs text-muted-foreground">{item?.day}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="pt-6 border-t border-border">
          <h3 className="text-base md:text-lg font-medium text-foreground mb-3 flex items-center gap-2">
            <Icon name="tag" size={18} className="text-accent" />
            Activity Categories
          </h3>
          <div className="space-y-3">
            {categoryBreakdown?.map((item) => (
              <div key={item?.category}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-foreground">{item?.category}</span>
                  <span className="text-sm text-muted-foreground">
                    {item?.count} ({item?.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-accent h-full rounded-full"
                    style={{ width: `${item?.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchedulingInsights;